import { useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Icon } from "@legal-500/legal500-design-system";
import { PageHeader } from "@/components/PageHeader/PageHeader.js";
import LoadingState from "@/components/LoadingState/index.js";
import { useQuery } from "@/hooks/useApi.js";
import { docParsing } from "@/api/docParsing.service.js";
import { ROUTES } from "@/routes.js";
import TimingBreakdown from "./TimingBreakdown.js";
import BarSummaryPanel from "./BarSummaryPanel.js";
import styles from "./ParsingJobs.module.css";

function ParsingJobDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const routeFileName = (location.state as { fileName?: string } | null)?.fileName;

  const { data, loading, error, refetch } = useQuery(() => docParsing.getResultById(id!), [id]);
  const { data: contextData } = useQuery(() => docParsing.getSubmissionContext(id!), [id]);

  const result = data?.result ?? null;
  const submissionContext = contextData?.context ?? null;
  const isActive = result?.status === "processing" || result?.status === "queued";

  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    if (!isActive) return;
    const interval = setInterval(() => refetchRef.current(), 5_000);
    return () => clearInterval(interval);
  }, [isActive]);

  const displayName = result?.fileName ?? routeFileName ?? "Parsing Job";

  return (
    <div className={styles.wrapper}>
      <PageHeader title={displayName} subtitle={id ?? ""}>
        <div style={{ display: "flex", gap: "12px" }}>
          <Button
            variant="outlined"
            color="default"
            onClick={() => navigate(ROUTES.submissions.documentParsing)}
          >
            <Icon icon="arrow-left" size="small" />
            Back
          </Button>
        </div>
      </PageHeader>

      {loading && <LoadingState message="Loading parsing job..." />}

      {error && !loading && (
        <div style={{ color: "var(--color-danger)", padding: "1rem 0" }}>
          Failed to load parsing job: {error.message}
        </div>
      )}

      {result && !loading && (
        <div className={styles.detailContent}>
          <div className={styles.detailLeftColumn}>
            <TimingBreakdown
              timeline={result.timeline}
              createdAt={result.createdAt}
              status={result.status}
              retryCount={result.retryCount}
            />
          </div>
          <div className={styles.detailRightColumn}>
            {submissionContext && (
              <div className={styles.jsonCard}>
                <h3 className={styles.jsonTitle}>Submission Context</h3>
                <dl className={styles.contextList}>
                  <div className={styles.contextRow}>
                    <dt>Original File</dt>
                    <dd>{submissionContext.originalFileName}</dd>
                  </div>
                  <div className={styles.contextRow}>
                    <dt>Firm</dt>
                    <dd>
                      {submissionContext.firmName}
                      {submissionContext.firmRef ? ` (${submissionContext.firmRef})` : ""}
                    </dd>
                  </div>
                  <div className={styles.contextRow}>
                    <dt>Publication</dt>
                    <dd>{submissionContext.publicationName}</dd>
                  </div>
                  {submissionContext.edition && (
                    <div className={styles.contextRow}>
                      <dt>Edition</dt>
                      <dd>{submissionContext.edition}</dd>
                    </div>
                  )}
                  {submissionContext.practiceAreaName && (
                    <div className={styles.contextRow}>
                      <dt>Practice Area</dt>
                      <dd>{submissionContext.practiceAreaName}</dd>
                    </div>
                  )}
                  {submissionContext.practiceGroupName && (
                    <div className={styles.contextRow}>
                      <dt>Practice Group</dt>
                      <dd>{submissionContext.practiceGroupName}</dd>
                    </div>
                  )}
                  {submissionContext.regionName && (
                    <div className={styles.contextRow}>
                      <dt>Region</dt>
                      <dd>{submissionContext.regionName}</dd>
                    </div>
                  )}
                  {submissionContext.tableName && (
                    <div className={styles.contextRow}>
                      <dt>Table</dt>
                      <dd>{submissionContext.tableName}</dd>
                    </div>
                  )}
                  <div className={styles.contextRow}>
                    <dt>Submission Type</dt>
                    <dd>{submissionContext.submissionType}</dd>
                  </div>
                  <div className={styles.contextRow}>
                    <dt>Referee Type</dt>
                    <dd>{submissionContext.refereeType}</dd>
                  </div>
                  {submissionContext.subtype && (
                    <div className={styles.contextRow}>
                      <dt>Subtype</dt>
                      <dd>{submissionContext.subtype}</dd>
                    </div>
                  )}
                </dl>
                <details className={styles.contextIds}>
                  <summary>IDs</summary>
                  <dl className={styles.contextList}>
                    {submissionContext.publicationId != null && (
                      <div className={styles.contextRow}>
                        <dt>Publication ID</dt>
                        <dd>{submissionContext.publicationId}</dd>
                      </div>
                    )}
                    {submissionContext.editorialCategoryId != null && (
                      <div className={styles.contextRow}>
                        <dt>Editorial Category ID</dt>
                        <dd>{submissionContext.editorialCategoryId}</dd>
                      </div>
                    )}
                    {submissionContext.practiceAreaId != null && (
                      <div className={styles.contextRow}>
                        <dt>Practice Area ID</dt>
                        <dd>{submissionContext.practiceAreaId}</dd>
                      </div>
                    )}
                    {submissionContext.practiceGroupId != null && (
                      <div className={styles.contextRow}>
                        <dt>Practice Group ID</dt>
                        <dd>{submissionContext.practiceGroupId}</dd>
                      </div>
                    )}
                    {submissionContext.regionId != null && (
                      <div className={styles.contextRow}>
                        <dt>Region ID</dt>
                        <dd>{submissionContext.regionId}</dd>
                      </div>
                    )}
                    {submissionContext.tableId != null && (
                      <div className={styles.contextRow}>
                        <dt>Table ID</dt>
                        <dd>{submissionContext.tableId}</dd>
                      </div>
                    )}
                  </dl>
                </details>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Full width, below the two columns: the barrister table is wider than the right column. */}
      {result && !loading && <BarSummaryPanel parsedJson={result.parsedJson} />}
    </div>
  );
}

export default ParsingJobDetail;
